import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { useRef } from 'react';
import {
    HrSimulationPayConstantPatchPayload,
    HrSimulationPayConstant,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/helpers/types';
import { invalidateGVTDataQuery } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/GVTData/helpers/hooks/gvtData';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import {
    RQAPICall,
    HttpError,
    HttpResponse,
    HttpContentWithData,
} from 'store/APIClient';
import { dispatch } from 'store/store';
import { resetResultSimulationDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/hooks/resultSimulationData';
import { invalidateAllSimulationAgentDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/hooks/agentsSimulation';
import { invalidateSimulationEnvelopeDataQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/NonRecurrentEnvelope/helpers/hooks/nonRecurrentEnvelope';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import { FreeHypothesisEditMutationParams } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import { invalidateSmicAgentsQueries } from '../../components/PayConstantsEntryForm/helpers/hooks/smic';

const PAY_CONSTANT_API_KEY = 'pay_constants';

export const useGetHrSimulationPayConstants = (
    hrSimulationId: number,
): UseQueryResult<HrSimulationPayConstant> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/pay_constants`;

    return useQuery({
        queryKey: [PAY_CONSTANT_API_KEY, hrSimulationId],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'GET',
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

export const usePutHrSimulationPayConstants = (
    hrSimulationId: number,
    changedHypotheses: string[],
    freeUpdatedHypotheses: FreeHypothesisEditMutationParams[],
) => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/pay_constants`;
    const queryClient = useQueryClient();

    const lastPayload = useRef<HrSimulationPayConstantPatchPayload | null>(
        null,
    );
    const lastChangedHypotheses = useRef<string[]>([]);
    if (freeUpdatedHypotheses.length > 0) {
        lastChangedHypotheses.current.push('ifse');
    }

    return useMutation({
        mutationFn: (
            payload: HrSimulationPayConstantPatchPayload,
        ): Promise<
            HttpResponse<HttpContentWithData<HrSimulationPayConstant>>
        > => {
            lastPayload.current = payload;
            lastChangedHypotheses.current = [
                ...lastChangedHypotheses.current,
                ...changedHypotheses,
            ];
            return RQAPICall({
                endpoint,
                method: 'PUT',
                body: {
                    smicHypothesis: payload.smicHypothesis,
                    rateCnraclHypothesis: payload.rateCnraclHypothesis,
                    indexPointHypothesis: payload.indexPointHypothesis,
                },
            });
        },

        onSuccess: () => {
            invalidateGVTDataQuery(queryClient, hrSimulationId);
            // as smic hypothesis might have changed, we need to invalidate the queries in order to keep the gvt data updated
            invalidateSmicAgentsQueries(queryClient, hrSimulationId);
            // as we have hypotheses changes, we reset the result queries
            resetResultSimulationDataQueries(queryClient, hrSimulationId);
            // We need to invalidate the queries in order to keep the hypothesis resume updated
            queryClient.invalidateQueries({
                queryKey: [PAY_CONSTANT_API_KEY, hrSimulationId],
            });

            invalidateAllSimulationAgentDataQueries(
                hrSimulationId,
                queryClient,
            );
            invalidateSimulationEnvelopeDataQueries(
                hrSimulationId,
                queryClient,
            );
            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.simulation.scenario
                    .updateHypotheses,
                {
                    smicHypothesis: lastPayload.current?.smicHypothesis.value,
                    rateCnraclHypothesis:
                        lastPayload.current?.rateCnraclHypothesis.value,
                    indexPointHypothesis:
                        lastPayload.current?.indexPointHypothesis.value,
                    typeHypothesis: Array.from(
                        // avoid duplicate element for the tracking event and format the tracking metadata
                        new Set(
                            lastChangedHypotheses.current.map((str) =>
                                str
                                    .replace(/Hypothesis$/, '')
                                    .replace(/([a-z])([A-Z])/g, '$1 $2'),
                            ),
                        ),
                    ).join(' et '),
                },
            );
            lastChangedHypotheses.current = [];
        },

        onError: (error: HttpError) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
            lastPayload.current = null;
        },
    });
};
